import React from 'react'
import {
  LineItemDTO,
  Link,
  useBasketLineItem,
  usePrice,
  LineItemDTOStatusEnum,
  useBasket,
  useGlobalLabels,
  useConfigState,
} from '@sentecacommerce-theme/lib'
import { LazyResponsiveImage } from '../LazyResponsiveImage'
import { Typography } from '../Typography'
import { WishlistButton } from '../WishlistButton'
import { Price } from '../Price'
import { QuantitySelector } from '../QuantitySelector'
import { RemoveFromBasketButton } from '../RemoveFromBasketButton'
import styled, { css } from 'styled-components'

export const BasketPreviewItem = ({
  data,
  productNumberLabel,
  errorRef,
  close,
}: {
  data: LineItemDTO
  productNumberLabel: string
  errorRef?: (el: HTMLDivElement | null) => void
  close?: () => void
}) => {
  const {
    name,
    sku,
    slug,
    lineItemId,
    images,
    productId,
    variantId,
    offerId,
    quantityInBasket,
    unitPrice: priceRaw,
    unitDiscountedPrice: discountedPriceRaw,
    totalPrice: totalPriceProp,
    totalDiscountedPrice: totalDiscountedPriceProp,
    inventory,
    status,
    selectedOptions,
    mappedSelectedOptions,
    getAttributeByKey,
    getCustomFieldByKey,
  } = useBasketLineItem({ item: data })
  const config = useConfigState()
  const selectedSize = getAttributeByKey(config.attributeKeys.size)

  const { setLineItemQuantity } = useBasket({
    productId,
    variantId,
    offerId: offerId as string,
    selectedOptions,
  })

  const hideRegularPrice = getCustomFieldByKey?.(
    config?.customFieldKeys?.hideRegularPrice
  )

  const getPrice = () => {
    if (!hideRegularPrice) return priceRaw

    if (discountedPriceRaw?.centAmount) return discountedPriceRaw

    return priceRaw
  }

  const { price, discountPrice } = usePrice({
    price: getPrice(),
    discountedPrice: !hideRegularPrice ? discountedPriceRaw : undefined,
  })

  const { price: totalPrice, discountPrice: totalDiscountedPrice } = usePrice({
    price: totalPriceProp,
    discountedPrice: totalDiscountedPriceProp,
  })

  const { errorMessages } = useGlobalLabels()

  const hasDiscount = Boolean(discountedPriceRaw)

  const isWithoutEnoughQuantity =
    status === LineItemDTOStatusEnum.NotEnoughQuantity
  const isOutOfStock = status === LineItemDTOStatusEnum.SoldOut
  const isWithoutOffer = status === LineItemDTOStatusEnum.NoOffer

  const canPurchase = status === LineItemDTOStatusEnum.CanPurchase

  const imageIndex = mappedSelectedOptions?.[0]?.value?.imageIndex || 0

  return (
    <Shadow
      ref={!canPurchase ? errorRef : undefined}
      isUnavailable={!canPurchase}
    >
      <Container>
        <ImageContainer>
          <Link href={`/${slug}/p`} passHref>
            <a>
              <LazyResponsiveImage
                imagesMap={images?.[imageIndex]}
                alt={`${name} - ${sku}`}
                breakpointSizes={{
                  xs: '160px',
                  sm: '200px',
                }}
                defaultImageWidth="250px"
              />
              <ButtonContainer className="d-none d-md-block">
                <WishlistButton productId={productId} variantId={variantId} />
              </ButtonContainer>
            </a>
          </Link>
        </ImageContainer>
        <Content>
          <TitleContainer>
            <Title variant="5">{name}</Title>
            <RemoveFromBasketButton
              lineItemId={lineItemId}
              variantId={variantId}
              productId={productId}
              close={close}
            />
          </TitleContainer>
          {mappedSelectedOptions?.map((option) => (
            <OptionContainer
              key={`Option__${option.label}--${option.value?.key}`}
            >
              <Typography variant="8" className="mr-2">
                {option.label}:
              </Typography>{' '}
              <BoldText>{option.value?.label}</BoldText>
            </OptionContainer>
          ))}
          <SKU>
            {productNumberLabel}: {sku}
          </SKU>
          {selectedSize ? (
            <OptionContainer>
              <Typography as="p" variant="8" className="mr-2">
                {selectedSize?.label?.toLowerCase()}:
              </Typography>
              <BoldText>{selectedSize?.value}</BoldText>
            </OptionContainer>
          ) : null}
          <Bottom>
            <QuantitySelectorContainer>
              <QtyAndMobilePriceContainer>
                <SelectAndWishlistContainerMobile>
                  <QuantitySelector
                    quantity={quantityInBasket}
                    maxQuantity={
                      inventory?.maxQuantity ?? config.inventory.maxQuantity
                    }
                    minQuantity={1}
                    onUpdate={setLineItemQuantity}
                  />
                  <ButtonContainer
                    mobile
                    className="d-flex align-items-center d-md-none ml-3"
                  >
                    <WishlistButton
                      productId={productId}
                      variantId={variantId}
                    />
                  </ButtonContainer>
                </SelectAndWishlistContainerMobile>
                <MobilePriceContainer>
                  <Price
                    price={price?.value}
                    discountedPrice={discountPrice?.value}
                    hideDiscountPercentage
                    size="md"
                    currency={price?.currency}
                  />
                </MobilePriceContainer>
              </QtyAndMobilePriceContainer>
            </QuantitySelectorContainer>
            <PriceContainer>
              <Price
                price={price.value}
                size="small"
                hideDiscountPercentage
                discountedPrice={
                  discountPrice ? discountPrice.value : undefined
                }
                currency={price.currency}
                hideRegularPrice={hideRegularPrice}
              />
            </PriceContainer>
            <TotalPriceContainer hasDiscount={hasDiscount}>
              <Price
                price={totalDiscountedPrice?.value ?? totalPrice.value}
                hideDiscountPercentage
                size="md"
                currency={totalPrice.currency}
              />
            </TotalPriceContainer>
          </Bottom>
        </Content>
        {isWithoutEnoughQuantity && (
          <ErrorMessage>{errorMessages?.notEnoughQty}</ErrorMessage>
        )}
        {isOutOfStock && (
          <ErrorMessage>{errorMessages?.outOfStockBasket}</ErrorMessage>
        )}
        {isWithoutOffer && (
          <ErrorMessage>{errorMessages?.outOfStock}</ErrorMessage>
        )}
      </Container>
    </Shadow>
  )
}

const Container = styled.div.attrs({
  className: 'row gx-5',
})`
  position: relative;
`

const OptionContainer = styled.div.attrs({
  className: 'd-flex align-items-center mb-2',
})`
  color: ${({ theme }) => theme.palette.greyTitle};
  p {
    font-size: 1.62rem;
    color: ${({ theme }) => theme.palette.primary};
  }
`

const BoldText = styled(Typography).attrs({ variant: '8' })`
  font-weight: 450;
`
const ErrorMessage = styled.div.attrs({
  className: 'py-0 px-4 mb-3 mx-auto mx-lg-0',
})`
  background-color: ${({ theme }) => theme.palette.danger};
  color: ${({ theme }) => theme.palette.secondary};
  font-weight: bold;
  font-size: 1.2rem;
  height: 2.4rem;
  text-align: center;
  text-transform: uppercase;
  line-height: 2.4rem;
  z-index: 100;
  border-radius: 0.4rem;
  width: calc(100% - 3rem);
  bottom: -1.4rem;
  position: relative;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg.min}) {
    position: absolute;
    bottom: -1rem;
    right: 1.2rem;
    width: fit-content;
  }
`

const Shadow = styled.div.attrs({
  className: 'p-4',
})<{ isUnavailable?: boolean }>`
  background: ${({ theme }) => theme.palette.secondary};
  border-radius: 1.4rem;
  box-shadow: ${(props) =>
    props.isUnavailable
      ? ` 0 0 0.4rem ${props.theme.palette.danger}`
      : ` 0 0 0.8rem ${props.theme.palette.lightgrey}`};
`

const ImageContainer = styled.a.attrs({
  className: 'col-5 col-md-4 d-block',
})`
  position: relative;
  border-right: 0.1rem solid ${({ theme }) => theme.palette.disabled};
`

const ButtonContainer = styled.div<{ mobile?: boolean }>`
  position: ${(props) => (props.mobile ? 'static' : 'absolute')};
  top: ${(props) => (props.mobile ? '11rem' : '0')};
  right: ${(props) => (props.mobile ? '0.4rem' : '0.6rem')};

  & button {
    padding: 0 !important;
    min-height: 0 !important;
    height: 2.5rem;
  }

  & svg {
    width: 2.5rem;

    position: relative;
    color: ${({ theme }) => theme.palette.greyTitle};
    transition: background-position 1s steps(28);
    transition-duration: 0s;
    transition: all 0.3s;

    &:hover {
      transform: scale(1.3);
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.md.min}) {
      width: 2.5rem;
      height: 2.5rem;
      top: -0.4rem;
    }
  }
`

const Content = styled.div.attrs({
  className: 'col-7 col-md-8',
})`
  display: flex;
  flex-direction: column;
`

const SKU = styled(Typography).attrs({
  as: 'span',
  variant: '8',
  color: 'hint',
  className: 'd-block mb-4 mt-2',
})`
  max-width: 80%;
  line-height: 1.2;
  margin-top: 0.6rem;
  font-size: 1.3rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.md.min}) {
    max-width: 100%;
    line-height: 1;
  }
`

const Title = styled(Typography).attrs({ className: 'mb-1' })`
  max-width: 90%;
  line-height: 1.2;
  padding-right: 1.5rem;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
`

const Bottom = styled.div.attrs({ className: 'pt-3' })`
  border-top: 0.1rem solid ${({ theme }) => theme.palette.disabled};

  @media (min-width: ${({ theme }) => theme.breakpoints.md.min}) {
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;
  }

  font-size: 2.5rem !important;
`

const QtyAndMobilePriceContainer = styled.div.attrs({
  className: 'd-flex flex-column align-items-start justify-content-between',
})`
  width: 100%;
`

const SelectAndWishlistContainerMobile = styled.div.attrs({
  className: 'd-flex align-items-center justify-content-between',
})`
  width: 100%;
  & svg {
    position: relative;
    top: 0.1rem;
  }
`

const MobilePriceContainer = styled.div.attrs({
  className: 'd-flex d-md-none align-items-center mt-5',
})`
  width: 100%;
`

const PriceContainer = styled.div.attrs({
  className: 'd-none d-md-flex align-items-center ml-4',
})``

const TitleContainer = styled.div.attrs({
  className: 'd-flex align-items-start justify-content-between',
})``

const TotalPriceContainer = styled.div.attrs({
  className: 'd-none d-md-flex align-items-center ml-auto',
})<{ hasDiscount?: boolean }>`
  ${(props) =>
    !props.hasDiscount &&
    css`
      & span {
        color: ${({ theme }) => theme.palette.primary};
      }
    `}
`

const QuantitySelectorContainer = styled.div.attrs({
  className: 'd-flex align-items-center justify-content-between',
})`
  width: 100%;
  max-width: 100%;

  position: relative;
  top: -0.11rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.md.min}) {
    width: auto;
    max-width: 13rem;
  }
`
